// kit — shared building blocks for the Agentic Scan report, all theme-token driven
// (no hex) so they track light/dark. Redesigned to the Zeus-crisp spec: caps
// micro-labels, a connected hairline stat-strip, and dense tint pills — expressed
// in corenet/Agate tokens.
import type { ReactNode } from "react";
import { theme, Typography } from "antd";
import { HintIcon } from "@/components/agate";
import type { Tone } from "@/lib/api/scanReport";

const { Text } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

function toneColors(token: ReturnType<typeof theme.useToken>["token"], tone: Tone) {
  switch (tone) {
    case "danger":
      return { bg: token.colorErrorBg, fg: token.colorError, bd: token.colorErrorBorder };
    case "warning":
      return { bg: token.colorWarningBg, fg: token.colorWarning, bd: token.colorWarningBorder };
    case "success":
      return { bg: token.colorSuccessBg, fg: token.colorSuccess, bd: token.colorSuccessBorder };
    case "info":
      return { bg: token.colorInfoBg, fg: token.colorInfo, bd: token.colorInfoBorder };
    default:
      return { bg: token.colorFillQuaternary, fg: token.colorTextSecondary, bd: token.colorBorderSecondary };
  }
}

// ToneTag — a dense tint pill (radius 4, 11px — corenet Tag metrics). The WORD
// carries the meaning; color only reinforces it. `dashed` marks a low-confidence /
// unverified signal.
export function ToneTag({
  tone,
  children,
  mono,
  dashed,
  title,
}: {
  tone: Tone;
  children: ReactNode;
  mono?: boolean;
  dashed?: boolean;
  title?: string;
}) {
  const { token } = theme.useToken();
  const c = toneColors(token, tone);
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: 11,
        lineHeight: "16px",
        fontFamily: mono ? MONO : undefined,
        background: c.bg,
        color: c.fg,
        border: `1px ${dashed ? "dashed" : "solid"} ${c.bd}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// Field — a label/value row for the identity/evaluation rail and the Logs tab.
export function Field({
  label,
  children,
  mono,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-32 shrink-0 text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">
        {label}
      </span>
      <span
        className="min-w-0 flex-1 break-words text-sm text-text-primary"
        style={mono ? { fontFamily: MONO, fontSize: 12 } : undefined}
      >
        {children}
      </span>
    </div>
  );
}

// Panel — a contained bordered section card (Zeus wraps each section in one). Holds
// a SectionHeader + content; tables inside read as proper line-by-line tables.
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-border bg-surface-elev p-5 ${className ?? ""}`}>{children}</section>
  );
}

// DefTable — a definition table: line-by-line label/value rows with hairline
// dividers, so metadata reads as a scannable table (Summary / Identity / Evaluation).
export function DefTable({
  rows,
  labelWidth = 132,
}: {
  rows: { label: string; value: ReactNode }[];
  labelWidth?: number;
}) {
  return (
    <div className="divide-y divide-border border-y border-border">
      {rows.map((r) => (
        <div
          key={r.label}
          className="grid items-baseline gap-4 py-2"
          style={{ gridTemplateColumns: `${labelWidth}px minmax(0,1fr)` }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">{r.label}</span>
          <span className="min-w-0 break-words text-sm text-text-primary">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// SectionHeader — the ▍ LABEL caps micro-header (3px brand accent bar + 11px caps).
// Replaces the serif CardShell titles inside the report; serif is reserved for the
// page H1. Promoted from corenet's own caps group-heading idiom.
export function SectionHeader({
  label,
  hint,
  extra,
}: {
  label: string;
  hint?: string;
  extra?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span aria-hidden className="h-3.5 w-[3px] shrink-0 rounded-sm bg-accent" />
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.6px] text-text-secondary">{label}</h3>
      {hint && <HintIcon hint={hint} />}
      {extra && <div className="ml-auto text-[11px] text-text-muted">{extra}</div>}
    </div>
  );
}

// RailHeader — the same caps recipe without the accent bar, for nested rail
// sub-sections (IDENTITY / EVALUATION / ENGINES …).
export function RailHeader({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">{children}</div>
  );
}

export interface StatTile {
  label: string;
  value: ReactNode;
  subLabel?: ReactNode;
  hint?: string;
  toneClass?: string;
  emphasized?: boolean;
}

// StatStrip — one connected hairline-divided band of equal cells (Zeus's headline
// strip), not a row of rounded cards. label → 22px value → muted sub-label.
export function StatStrip({ tiles }: { tiles: StatTile[] }) {
  const { token } = theme.useToken();
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface-elev">
      <div className="grid grid-cols-2 divide-x divide-y divide-border sm:grid-cols-3 lg:grid-cols-5 lg:divide-y-0">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="flex flex-col gap-1 p-4"
            style={t.emphasized ? { background: token.colorFillQuaternary } : undefined}
          >
            <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">
              {t.label}
              {t.hint && <HintIcon hint={t.hint} />}
            </div>
            <div
              className={`flex min-h-[32px] items-center text-[26px] font-semibold leading-none tabular-nums ${t.toneClass ?? "text-text-primary"}`}
              style={{ fontFamily: MONO }}
            >
              {t.value}
            </div>
            {t.subLabel != null && <div className="text-[11px] text-text-muted">{t.subLabel}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ScoreMeter — 0-100 capability/trust bar (thin track + number), colored by tier.
export function ScoreMeter({ score, tone }: { score: number | null; tone: Tone }) {
  const { token } = theme.useToken();
  const c = toneColors(token, tone);
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  return (
    <div className="flex flex-nowrap items-center gap-2">
      <div
        className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full"
        style={{ background: token.colorFillQuaternary }}
        role="img"
        aria-label={`score ${score ?? "unknown"} of 100`}
      >
        <div style={{ width: `${pct}%`, height: "100%", background: c.fg }} />
      </div>
      <Text
        className="shrink-0 tabular-nums"
        style={{ fontSize: 12, color: token.colorTextSecondary, whiteSpace: "nowrap" }}
      >
        {score == null ? "—" : score}
      </Text>
    </div>
  );
}

// Th — caps micro-label for AntD column titles (keeps AntD's own sort carets).
export function Th(s: string) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-text-muted">{s}</span>
  );
}
